import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { isMockMode } from '@/api';
import { rich, useT } from '@/i18n/runtime';
import { useConsent } from '@/media/useConsent';
import { useSessionStore } from '@/store/sessionStore';
import type { StepProps } from '../types';
import { AppBar } from '../components/AppBar';
import { Stepper } from '../components/Stepper';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { KNOWN, PURPOSE_META } from '../components/ConsentGate';

/**
 * SCREEN 02 · CONSENT — the REAL, binding DPDP ceremony, captured BEFORE any
 * document/biometric capture (S7-6 fix: the previous build posted the server
 * consent record only at the LiveCall gate, which put capture AHEAD of recorded
 * consent — a DPDP ordering bug). In real-backend mode this step runs the full
 * useConsent ceremony (versioned text, UNRATIFIED stamp shown verbatim,
 * explicit affirmative, decline blocks, 409-already-captured resolves quietly)
 * and only advances the flow once the record is on the server. The LiveCall
 * ConsentGate stays as a re-post → 409 → instantly-resolved backstop, by design.
 *
 * Mock mode (no backend) keeps the previous notice-only behaviour: the customer
 * still sees the ceremony copy and affirms, but nothing is posted — no evidence
 * rail exists to bind a record to.
 */
export function Consent({ tenant }: StepProps): JSX.Element {
  const { t } = useT();
  const real = !isMockMode();
  const sessionId = useSessionStore((s) => s.session.sessionId) as string;
  const setAffirmed = useSessionStore((s) => s.setConsentAffirmed);
  const advance = useSessionStore((s) => s.completeCurrentAndAdvance);
  const gate = useConsent(sessionId, real);

  // Local tick — the explicit affirmative. The store guard is set ONLY from the
  // resolved server ceremony in real mode (never from the checkbox alone).
  const [ticked, setTicked] = useState(false);
  const advanced = useRef(false);

  useEffect(() => {
    if (!real || !gate.resolved || advanced.current) return;
    advanced.current = true;
    setAffirmed(true); // guard reflects the recorded (or already-recorded) consent
    advance();
  }, [real, gate.resolved, setAffirmed, advance]);

  const toggle = () => setTicked((v) => !v);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      toggle();
    }
  };

  if (!real) return <MockConsent tenant={tenant} />;

  const busy = gate.phase === 'posting' || gate.phase === 'captured';

  // ── declined: honest block, no dead end ─────────────────────────────────────
  if (gate.phase === 'declined') {
    return (
      <div className="app-view">
        <AppBar tenant={tenant} subtitle={t('droom.consent.subtitle')} />
        <Stepper current="consent" />
        <div className="app-view center" style={{ paddingTop: 24 }}>
          <div className="center-col" style={{ maxWidth: '34ch' }}>
            <span className="consent-gate-badge">
              <Icon name="lock" size={28} />
            </span>
            <div className="h-title sm">{t('droom.consent.declined.title')}</div>
            <p className="sub" style={{ marginTop: 8 }}>
              {t('droom.consent.declined.body', { tenantName: tenant.name })}
            </p>
            <Button style={{ marginTop: 18 }} onClick={gate.reconsider}>
              {t('droom.consent.declined.review')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── loading / error / resolved beat ─────────────────────────────────────────
  if (gate.phase === 'loading' || gate.phase === 'error' || gate.phase === 'resolved' || !gate.text) {
    return (
      <div className="app-view">
        <AppBar tenant={tenant} subtitle={t('droom.consent.subtitle')} />
        <Stepper current="consent" />
        <div className="app-view center" style={{ paddingTop: 24 }}>
          <div className="center-col" style={{ maxWidth: '32ch' }}>
            {gate.phase === 'error' ? (
              <>
                <div className="h-title sm">{t('droom.consent.loadfail')}</div>
                <p className="sub" style={{ marginTop: 8 }}>
                  {gate.detail ? t(gate.detail) : t('droom.error.tryAgain')}
                </p>
                <Button style={{ marginTop: 18 }} onClick={gate.retry}>
                  {t('droom.action.tryAgain')}
                </Button>
              </>
            ) : (
              <p className="sub" role="status">
                {gate.phase === 'resolved'
                  ? t('droom.consent.resolved')
                  : t('droom.consent.loading')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const text = gate.text;
  const purposes = text.purposes.filter(KNOWN);

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('droom.consent.subtitle')} />
      <Stepper current="consent" />

      <div className="stack">
        <div>
          <div className="h-title sm">{t('droom.consent.title')}</div>
          <p className="sub-sm" style={{ marginTop: 6 }}>
            {t('droom.consent.sub', { regime: tenant.regime })}
          </p>
        </div>

        <div className="card notice-summary">
          <div className="notice-head">
            <span style={{ fontWeight: 700, fontSize: 'var(--fs-cap)' }}>
              {t('droom.consent.process.head')}
            </span>
            <span
              className="v mono"
              title={
                text.ratified
                  ? t('droom.consent.version.title')
                  : t('droom.consent.version.unratified')
              }
            >
              {text.consentVersion}
            </span>
          </div>
          {purposes.map((k) => {
            const m = PURPOSE_META[k];
            return (
              <div className="purpose" key={k}>
                <span className="pic">
                  <Icon name={m.icon} size={16} />
                </span>
                <div>
                  <div className="pt">{t(m.titleKey)}</div>
                  <div className="pd">{t(m.descKey)}</div>
                </div>
              </div>
            );
          })}
        </div>

        {!text.ratified ? (
          <div className="consent-unratified mono" role="note">
            {t('droom.consent.unratified', { version: text.consentVersion })}
          </div>
        ) : null}

        <details className="disclosure">
          <summary>
            {t('droom.consent.readFull')}
            <span className="chev">
              <Icon name="chevron" size={16} />
            </span>
          </summary>
          <div className="body">{text.noticeBody}</div>
        </details>

        <div
          className="consent-toggle"
          data-on={ticked ? 'true' : 'false'}
          role="checkbox"
          aria-checked={ticked}
          tabIndex={0}
          onClick={toggle}
          onKeyDown={onKey}
        >
          <span className="box">
            <Icon name="check" size={14} />
          </span>
          <span className="ct">
            {rich(t('droom.consent.affirm', { tenantName: tenant.name }))}
          </span>
        </div>

        {gate.detail ? (
          <div className="consent-note mono" role="status">
            {t(gate.detail)}
          </div>
        ) : null}
      </div>

      <div className="spacer" />

      <div className="stack-sm" style={{ marginTop: 16 }}>
        <Button onClick={gate.affirm} disabled={!ticked || busy}>
          {gate.phase === 'captured'
            ? t('droom.consent.recorded')
            : busy
              ? t('droom.consent.recording')
              : t('droom.consent.agree')}
        </Button>
        <Button variant="ghost" onClick={gate.decline} disabled={busy}>
          {t('droom.consent.notNow')}
        </Button>
        <div className="cobrand mono" style={{ fontSize: 11 }}>
          {t('droom.consent.stampline')}
        </div>
      </div>
    </div>
  );
}

/**
 * Mock/demo-mode consent — the previous notice-only screen, unchanged: the
 * toggle drives the flow guard directly and nothing is posted anywhere.
 */
function MockConsent({ tenant }: StepProps): JSX.Element {
  const { t } = useT();
  const affirmed = useSessionStore((s) => s.guards.consentAffirmed);
  const setAffirmed = useSessionStore((s) => s.setConsentAffirmed);
  const advance = useSessionStore((s) => s.completeCurrentAndAdvance);

  const toggle = () => setAffirmed(!affirmed);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle="Video-KYC" />
      <Stepper current="consent" />

      <div className="stack">
        <div>
          <div className="h-title sm">Your privacy, your consent</div>
          <p className="sub-sm" style={{ marginTop: 6 }}>
            {t('droom.consent.sub', { regime: tenant.regime })}
          </p>
        </div>

        <div className="card notice-summary">
          <div className="notice-head">
            <span style={{ fontWeight: 700, fontSize: 'var(--fs-cap)' }}>
              What we collect
            </span>
            <span className="v mono">Summary</span>
          </div>
          <div className="purpose">
            <span className="pic">
              <Icon name="doc" size={16} />
            </span>
            <div>
              <div className="pt">Identity documents</div>
              <div className="pd">PAN &amp; Aadhaar / passport — to confirm who you are.</div>
            </div>
          </div>
          <div className="purpose">
            <span className="pic">
              <Icon name="face" size={16} />
            </span>
            <div>
              <div className="pt">Face &amp; liveness</div>
              <div className="pd">A live selfie and short video, matched to your ID.</div>
            </div>
          </div>
          <div className="purpose">
            <span className="pic">
              <Icon name="mappin" size={16} />
            </span>
            <div>
              <div className="pt">Location at capture</div>
              <div className="pd">To confirm you're in India, as RBI requires.</div>
            </div>
          </div>
        </div>

        <details className="disclosure">
          <summary>
            Read the full DPDP notice
            <span className="chev">
              <Icon name="chevron" size={16} />
            </span>
          </summary>
          <div className="body">
            Your data is processed by KycHub Technologies on behalf of{' '}
            {tenant.name} as Data Fiduciary, retained per the RBI Master Direction
            on KYC and the DPDP Act 2023, and never sold. You may withdraw consent
            or request erasure after the mandatory retention window.
          </div>
        </details>

        <div
          className="consent-toggle"
          data-on={affirmed ? 'true' : 'false'}
          role="checkbox"
          aria-checked={affirmed}
          tabIndex={0}
          onClick={toggle}
          onKeyDown={onKey}
        >
          <span className="box">
            <Icon name="check" size={14} />
          </span>
          <span className="ct">
            I <b>consent</b> to KycHub processing my identity data to complete{' '}
            {tenant.name}'s Video-KYC verification.
          </span>
        </div>
      </div>

      <div className="spacer" />

      <div className="stack-sm" style={{ marginTop: 16 }}>
        <Button onClick={advance} disabled={!affirmed}>
          Agree &amp; continue
        </Button>
        <Button variant="ghost">Not now</Button>
        <div className="cobrand mono" style={{ fontSize: 11 }}>
          Formal consent is recorded before your live call · immutable
        </div>
      </div>
    </div>
  );
}
